import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ordersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Printer,
  Search,
  CheckSquare,
  Square,
  Tag,
  Package,
  X,
  FileText,
  AlertCircle,
  Filter,
  Keyboard,
  ChevronDown,
  ChevronUp,
  Car,
  Plus,
  Trash2,
} from "lucide-react";

// Import mappatura Google Drive
import labelMapping from "@/data/labelMapping.json";

// ============== CONFIGURAZIONE EPSON COLORWORKS C4000e ==============
// Configurazione specifica per ogni formato di etichetta
const LABEL_CONFIG = {
  "30ml": {
    widthMM: 40,      // Larghezza etichetta
    heightMM: 60,     // Altezza etichetta
    orientation: "portrait",  // Verticale
    autoCut: false,   // NO taglio automatico
    paperGroup: "small",
  },
  "50ml": {
    widthMM: 60,      // Larghezza (ruotato)
    heightMM: 40,     // Altezza (ruotato)
    orientation: "landscape", // Orizzontale
    autoCut: false,   // NO taglio automatico
    paperGroup: "small",
  },
  "100ml": {
    widthMM: 80,      // Larghezza etichetta
    heightMM: 101,    // Altezza etichetta
    orientation: "portrait",  // Verticale
    autoCut: true,    // Taglio dopo ogni etichetta
    paperGroup: "large",
  },
  "200ml": {
    widthMM: 80,      // Larghezza etichetta
    heightMM: 101,    // Altezza etichetta
    orientation: "portrait",  // Verticale
    autoCut: true,    // Taglio dopo ogni etichetta
    paperGroup: "large",
  },
  "500ml": {
    widthMM: 80,
    heightMM: 101,
    orientation: "portrait",
    autoCut: true,
    paperGroup: "large",
  },
};

// Format groupings for different label paper
const PAPER_GROUPS = {
  small: {
    name: "Rotolo A (30ml + 50ml)",
    formats: ["30ml", "50ml"],
    color: "bg-blue-100 text-blue-700",
    description: "Die-cut 40x60mm - Uscita continua senza taglio",
  },
  large: {
    name: "Rotolo B (100ml + 200ml + 500ml)",
    formats: ["100ml", "200ml", "500ml"],
    color: "bg-green-100 text-green-700",
    description: "Die-cut 80x101mm - Taglio automatico",
  },
};

// DATABASE COMPLETO ETICHETTE - 228 codici mappati
// Ogni codice ha il path esatto dell'immagine per ogni formato disponibile
const LABELS_DATABASE = {
  "#31#": { "100ml": "/etichette/100ml_img/#31#.png" },
  "001": { "200ml": "/etichette/200ml_img/001.png" },
  "1002": { "100ml": "/etichette/100ml_img/1002.png" },
  "1006": { "100ml": "/etichette/100ml-nuovo_img/1006.png" },
  "1007": { "100ml": "/etichette/100ml-nuovo_img/1007.png" },
  "101": { "200ml": "/etichette/200ml_img/101.png" },
  "1010": { "30ml": "/etichette/30ml_img/1010.png" },
  "1012": { "100ml": "/etichette/100ml-nuovo_img/1012.png", "30ml": "/etichette/30ml_img/1012.png" },
  "1013": { "200ml": "/etichette/200ml_img/1013.png" },
  "1018": { "100ml": "/etichette/100ml-nuovo_img/1018.png" },
  "102": { "100ml": "/etichette/100ml_img/102.png" },
  "1021": { "100ml": "/etichette/100ml_img/1021.png" },
  "1023": { "100ml": "/etichette/100ml_img/1023.png" },
  "1029": { "100ml": "/etichette/100ml-nuovo_img/1029.png", "200ml": "/etichette/200ml_img/1029.png" },
  "1031": { "200ml": "/etichette/200ml_img/1031.png" },
  "1032": { "30ml": "/etichette/30ml_img/1032.png" },
  "1033": { "100ml": "/etichette/100ml-nuovo_img/1033.png", "200ml": "/etichette/200ml_img/1033.png", "30ml": "/etichette/30ml_img/1033.png" },
  "1041": { "200ml": "/etichette/200ml_img/1041.png" },
  "1044": { "200ml": "/etichette/200ml_img/1044.png" },
  "1045": { "100ml": "/etichette/100ml-nuovo_img/1045.png" },
  "1047": { "30ml": "/etichette/30ml_img/1047.png" },
  "1055": { "30ml": "/etichette/30ml_img/1055.png" },
  "1057": { "200ml": "/etichette/200ml_img/1057.png", "30ml": "/etichette/30ml_img/1057.png" },
  "1058": { "30ml": "/etichette/30ml_img/1058.png" },
  "1065": { "100ml": "/etichette/100ml_img/1065.png" },
  "1066": { "100ml": "/etichette/100ml-nuovo_img/1066.png", "200ml": "/etichette/200ml_img/1066.png" },
  "1069": { "30ml": "/etichette/30ml_img/1069.png" },
  "107": { "100ml": "/etichette/100ml_img/107.png" },
  "1070": { "30ml": "/etichette/30ml_img/1070.png" },
  "1075": { "100ml": "/etichette/100ml_img/1075.png" },
  "1076": { "200ml": "/etichette/200ml_img/1076.png" },
  "1080": { "100ml": "/etichette/100ml-nuovo_img/1080.png", "30ml": "/etichette/30ml_img/1080.png" },
  "1081": { "100ml": "/etichette/100ml_img/1081.png", "200ml": "/etichette/200ml_img/1081.png", "30ml": "/etichette/30ml_img/1081.png" },
  "1082": { "100ml": "/etichette/100ml_img/1082.png" },
  "1085": { "200ml": "/etichette/200ml_img/1085.png" },
  "1097": { "100ml": "/etichette/100ml-nuovo_img/1097.png" },
  "1098": { "100ml": "/etichette/100ml-nuovo_img/1098.png", "200ml": "/etichette/200ml_img/1098.png" },
  "11": { "200ml": "/etichette/200ml_img/11.png" },
  "1105": { "200ml": "/etichette/200ml_img/1105.png", "30ml": "/etichette/30ml_img/1105.png" },
  "1106": { "30ml": "/etichette/30ml_img/1106.png" },
  "112": { "30ml": "/etichette/30ml_img/112.png" },
  "113": { "30ml": "/etichette/30ml_img/113.png" },
  "114": { "30ml": "/etichette/30ml_img/114.png" },
  "116": { "100ml": "/etichette/100ml_img/116.png", "50ml": "/etichette/50ml_img/116.png" },
  "117": { "200ml": "/etichette/200ml_img/117.png", "50ml": "/etichette/50ml_img/117.png" },
  "12": { "50ml": "/etichette/50ml_img/12.png" },
  "125": { "100ml": "/etichette/100ml_img/125.png" },
  "129": { "100ml": "/etichette/100ml-nuovo_img/129.png" },
  "133": { "50ml": "/etichette/50ml_img/133.png" },
  "134": { "50ml": "/etichette/50ml_img/134.png" },
  "136": { "100ml": "/etichette/100ml_img/136.png" },
  "137": { "200ml": "/etichette/200ml_img/137.png" },
  "138": { "100ml": "/etichette/100ml-nuovo_img/138.png", "200ml": "/etichette/200ml_img/138.png" },
  "140": { "30ml": "/etichette/30ml_img/140.png" },
  "142": { "30ml": "/etichette/30ml_img/142.png" },
  "143": { "30ml": "/etichette/30ml_img/143.png" },
  "148": { "100ml": "/etichette/100ml_img/148.png" },
  "15": { "50ml": "/etichette/50ml_img/15.png" },
  "155": { "100ml": "/etichette/100ml-nuovo_img/155.png" },
  "156": { "200ml": "/etichette/200ml_img/156.png" },
  "158": { "100ml": "/etichette/100ml-nuovo_img/158.png" },
  "159": { "100ml": "/etichette/100ml_img/159.png" },
  "16": { "200ml": "/etichette/200ml_img/16.png" },
  "164": { "100ml": "/etichette/100ml_img/164.png" },
  "167": { "200ml": "/etichette/200ml_img/167.png" },
  "171": { "100ml": "/etichette/100ml-nuovo_img/171.png" },
  "172": { "30ml": "/etichette/30ml_img/172.png" },
  "174": { "100ml": "/etichette/100ml_img/174.png", "30ml": "/etichette/30ml_img/174.png", "50ml": "/etichette/50ml_img/174.png" },
  "175": { "30ml": "/etichette/30ml_img/175.png", "50ml": "/etichette/50ml_img/175.png" },
  "177": { "50ml": "/etichette/50ml_img/177.png" },
  "178": { "200ml": "/etichette/200ml_img/178.png" },
  "18": { "100ml": "/etichette/100ml_img/18.png", "30ml": "/etichette/30ml_img/18.png" },
  "182": { "200ml": "/etichette/200ml_img/182.png" },
  "184": { "100ml": "/etichette/100ml_img/184.png" },
  "185": { "200ml": "/etichette/200ml_img/185.png" },
  "187": { "200ml": "/etichette/200ml_img/187.png" },
  "189": { "100ml": "/etichette/100ml-nuovo_img/189.png", "200ml": "/etichette/200ml_img/189.png" },
  "19": { "30ml": "/etichette/30ml_img/19.png" },
  "193": { "200ml": "/etichette/200ml_img/193.png" },
  "198": { "30ml": "/etichette/30ml_img/198.png" },
  "199": { "30ml": "/etichette/30ml_img/199.png" },
  "2": { "100ml": "/etichette/100ml-nuovo_img/2.png", "200ml": "/etichette/200ml_img/2.png" },
  "20": { "100ml": "/etichette/100ml_img/20.png", "30ml": "/etichette/30ml_img/20.png" },
  "200": { "30ml": "/etichette/30ml_img/200.png" },
  "2002": { "200ml": "/etichette/200ml_img/2002.png", "30ml": "/etichette/30ml_img/2002.png" },
  "2003": { "200ml": "/etichette/200ml_img/2003.png" },
  "2004": { "100ml": "/etichette/100ml-nuovo_img/2004.png", "200ml": "/etichette/200ml_img/2004.png" },
  "2005": { "200ml": "/etichette/200ml_img/2005.png" },
  "2010": { "200ml": "/etichette/200ml_img/2010.png" },
  "2012": { "100ml": "/etichette/100ml-nuovo_img/2012.png", "200ml": "/etichette/200ml_img/2012.png" },
  "2014": { "100ml": "/etichette/100ml_img/2014.png" },
  "2015": { "200ml": "/etichette/200ml_img/2015.png", "30ml": "/etichette/30ml_img/2015.png" },
  "2016": { "30ml": "/etichette/30ml_img/2016.png" },
  "2018": { "200ml": "/etichette/200ml_img/2018.png", "30ml": "/etichette/30ml_img/2018.png" },
  "2019": { "200ml": "/etichette/200ml_img/2019.png", "30ml": "/etichette/30ml_img/2019.png" },
  "2020": { "100ml": "/etichette/100ml_img/2020.png" },
  "2024": { "100ml": "/etichette/100ml_img/2024.png" },
  "204": { "100ml": "/etichette/100ml_img/204.png" },
  "205": { "200ml": "/etichette/200ml_img/205.png", "50ml": "/etichette/50ml_img/205.png" },
  "206": { "50ml": "/etichette/50ml_img/206.png" },
  "208": { "200ml": "/etichette/200ml_img/208.png" },
  "212": { "100ml": "/etichette/100ml-nuovo_img/212.png", "200ml": "/etichette/200ml_img/212.png" },
  "215": { "100ml": "/etichette/100ml_img/215.png" },
  "218": { "30ml": "/etichette/30ml_img/218.png" },
  "219": { "200ml": "/etichette/200ml_img/219.png", "30ml": "/etichette/30ml_img/219.png" },
  "220": { "100ml": "/etichette/100ml_img/220.png" },
  "222": { "100ml": "/etichette/100ml-nuovo_img/222.png", "200ml": "/etichette/200ml_img/222.png" },
  "223": { "200ml": "/etichette/200ml_img/223.png" },
  "224": { "200ml": "/etichette/200ml_img/224.png" },
  "225": { "100ml": "/etichette/100ml-nuovo_img/225.png" },
  "226": { "50ml": "/etichette/50ml_img/226.png" },
  "227": { "50ml": "/etichette/50ml_img/227.png" },
  "228": { "50ml": "/etichette/50ml_img/228.png" },
  "23": { "100ml": "/etichette/100ml_img/23.png" },
  "231": { "30ml": "/etichette/30ml_img/231.png" },
  "232": { "30ml": "/etichette/30ml_img/232.png" },
  "233": { "30ml": "/etichette/30ml_img/233.png" },
  "235": { "100ml": "/etichette/100ml_img/235.png" },
  "237": { "200ml": "/etichette/200ml_img/237.png" },
  "239": { "100ml": "/etichette/100ml_img/239.png" },
  "24": { "100ml": "/etichette/100ml_img/24.png" },
  "244": { "100ml": "/etichette/100ml-nuovo_img/244.png" },
  "247": { "100ml": "/etichette/100ml-nuovo_img/247.png" },
  "248": { "30ml": "/etichette/30ml_img/248.png" },
  "250": { "30ml": "/etichette/30ml_img/250.png" },
  "251": { "50ml": "/etichette/50ml_img/251.png" },
  "254": { "50ml": "/etichette/50ml_img/254.png" },
  "27": { "100ml": "/etichette/100ml_img/27.png" },
  "28": { "100ml": "/etichette/100ml_img/28.png" },
  "36": { "100ml": "/etichette/100ml_img/36.png" },
  "38": { "100ml": "/etichette/100ml_img/38.png" },
  "40": { "50ml": "/etichette/50ml_img/40.png" },
  "413": { "100ml": "/etichette/100ml-nuovo_img/413.png", "200ml": "/etichette/200ml_img/413.png" },
  "417": { "200ml": "/etichette/200ml_img/417.png" },
  "419": { "100ml": "/etichette/100ml_img/419.png" },
  "42": { "50ml": "/etichette/50ml_img/42.png" },
  "420": { "100ml": "/etichette/100ml-nuovo_img/420.png", "200ml": "/etichette/200ml_img/420.png" },
  "427": { "30ml": "/etichette/30ml_img/427.png" },
  "429": { "30ml": "/etichette/30ml_img/429.png" },
  "45": { "100ml": "/etichette/100ml_img/45.png" },
  "48": { "100ml": "/etichette/100ml_img/48.png" },
  "519": { "100ml": "/etichette/100ml-nuovo_img/519.png", "30ml": "/etichette/30ml_img/519.png" },
  "520": { "100ml": "/etichette/100ml-nuovo_img/520.png" },
  "527": { "200ml": "/etichette/200ml_img/527.png" },
  "53": { "100ml": "/etichette/100ml_img/53.png", "30ml": "/etichette/30ml_img/53.png" },
  "534": { "100ml": "/etichette/100ml_img/534.png", "30ml": "/etichette/30ml_img/534.png" },
  "535": { "30ml": "/etichette/30ml_img/535.png" },
  "536": { "30ml": "/etichette/30ml_img/536.png" },
  "538": { "100ml": "/etichette/100ml_img/538.png", "200ml": "/etichette/200ml_img/538.png" },
  "539": { "100ml": "/etichette/100ml_img/539.png", "200ml": "/etichette/200ml_img/539.png" },
  "54": { "30ml": "/etichette/30ml_img/54.png" },
  "550": { "100ml": "/etichette/100ml-nuovo_img/550.png", "200ml": "/etichette/200ml_img/550.png", "30ml": "/etichette/30ml_img/550.png" },
  "552": { "100ml": "/etichette/100ml-nuovo_img/552.png", "30ml": "/etichette/30ml_img/552.png" },
  "557": { "100ml": "/etichette/100ml-nuovo_img/557.png" },
  "558": { "30ml": "/etichette/30ml_img/558.png" },
  "559": { "30ml": "/etichette/30ml_img/559.png" },
  "56": { "30ml": "/etichette/30ml_img/56.png" },
  "560": { "30ml": "/etichette/30ml_img/560.png" },
  "57": { "200ml": "/etichette/200ml_img/57.png" },
  "570": { "200ml": "/etichette/200ml_img/570.png" },
  "592": { "200ml": "/etichette/200ml_img/592.png" },
  "6212": { "100ml": "/etichette/100ml_img/6212.png" },
  "6242": { "100ml": "/etichette/100ml_img/6242.png" },
  "66": { "100ml": "/etichette/100ml-nuovo_img/66.png", "200ml": "/etichette/200ml_img/66.png" },
  "69": { "200ml": "/etichette/200ml_img/69.png" },
  "7": { "100ml": "/etichette/100ml_img/7.png", "200ml": "/etichette/200ml_img/7.png" },
  "7003": { "200ml": "/etichette/200ml_img/7003.png" },
  "7004": { "100ml": "/etichette/100ml-nuovo_img/7004.png" },
  "7008": { "30ml": "/etichette/30ml_img/7008.png" },
  "7009": { "30ml": "/etichette/30ml_img/7009.png" },
  "7010": { "200ml": "/etichette/200ml_img/7010(1).png", "30ml": "/etichette/30ml_img/7010.png" },
  "7011": { "100ml": "/etichette/100ml_img/7011.png" },
  "71": { "200ml": "/etichette/200ml_img/71.png" },
  "75": { "50ml": "/etichette/50ml_img/75.png" },
  "76": { "100ml": "/etichette/100ml_img/76.png", "50ml": "/etichette/50ml_img/76.png" },
  "8": { "100ml": "/etichette/100ml_img/8.png" },
  "8000": { "200ml": "/etichette/200ml_img/8000.png" },
  "8005": { "100ml": "/etichette/100ml-nuovo_img/8005.png" },
  "8006": { "100ml": "/etichette/100ml-nuovo_img/8006.png" },
  "8009": { "100ml": "/etichette/100ml-nuovo_img/8009.png" },
  "82": { "30ml": "/etichette/30ml_img/82.png" },
  "83": { "30ml": "/etichette/30ml_img/83.png" },
  "85": { "100ml": "/etichette/100ml-nuovo_img/85.png", "30ml": "/etichette/30ml_img/85.png" },
  "93": { "100ml": "/etichette/100ml-nuovo_img/93.png" },
  "98": { "100ml": "/etichette/100ml-nuovo_img/98.png" },
  "E19": { "100ml": "/etichette/100ml-nuovo_img/E19.png", "30ml": "/etichette/30ml_img/E19.png" },
  "G46": { "30ml": "/etichette/30ml_img/G46.png" },
  "GA912": { "200ml": "/etichette/200ml_img/GA912.png" },
  "GA916": { "100ml": "/etichette/100ml_img/GA916.png" },
  "GA941": { "100ml": "/etichette/100ml_img/GA941.png" },
  "GA952": { "200ml": "/etichette/200ml_img/GA952.png" },
  "M56": { "100ml": "/etichette/100ml-nuovo_img/M56.png" },
  "M57": { "200ml": "/etichette/200ml_img/M57.png" },
  "M61": { "30ml": "/etichette/30ml_img/M61.png" },
  "M63": { "30ml": "/etichette/30ml_img/M63.png" },
  "VF1016": { "200ml": "/etichette/200ml_img/VF1016.png" },
  "VF172": { "200ml": "/etichette/200ml_img/VF172.png" },
  "VF2008": { "100ml": "/etichette/100ml-nuovo_img/VF2008.png", "200ml": "/etichette/200ml_img/VF2008.png" },
  "VF240": { "100ml": "/etichette/100ml-nuovo_img/VF240.png" },
  "VF6001": { "100ml": "/etichette/100ml-nuovo_img/VF6001.png" },
  "VF6006": { "30ml": "/etichette/30ml_img/VF6006.png" },
  "VF6007": { "200ml": "/etichette/200ml_img/VF6007.png", "30ml": "/etichette/30ml_img/VF6007.png" },
  "VF610": { "200ml": "/etichette/200ml_img/VF610.png" },
  "VF6106": { "100ml": "/etichette/100ml_img/VF6106.png" },
  "VF6135": { "200ml": "/etichette/200ml_img/VF6135.png" },
  "VF6143": { "200ml": "/etichette/200ml_img/VF6143.png" },
  "VF6161": { "200ml": "/etichette/200ml_img/VF6161.png" },
  "VF6162": { "100ml": "/etichette/100ml-nuovo_img/VF6162.png", "30ml": "/etichette/30ml_img/VF6162.png" },
  "VF6164": { "30ml": "/etichette/30ml_img/VF6164.png" },
  "VF6174": { "200ml": "/etichette/200ml_img/VF6174.png" },
  "VF6211": { "200ml": "/etichette/200ml_img/VF6211.png", "30ml": "/etichette/30ml_img/VF6211.png" },
  "VF6224": { "30ml": "/etichette/30ml_img/VF6224.png" },
  "VF6240": { "200ml": "/etichette/200ml_img/VF6240.png" },
  "VF6241": { "200ml": "/etichette/200ml_img/VF6241.png" },
  "VF681": { "200ml": "/etichette/200ml_img/VF681.png" },
  "VF7008": { "100ml": "/etichette/100ml_img/VF7008.png" },
  "VF7024": { "100ml": "/etichette/100ml-nuovo_img/VF7024.png" },
  "VF7027": { "200ml": "/etichette/200ml_img/VF7027.png" },
  "VF7057": { "100ml": "/etichette/100ml_img/VF7057.png" },
  "VF7062": { "200ml": "/etichette/200ml_img/VF7062.png" },
  "VF7147": { "30ml": "/etichette/30ml_img/VF7147.png" },
  "VF7158": { "30ml": "/etichette/30ml_img/VF7158.png" },
  "VF7159": { "200ml": "/etichette/200ml_img/VF7159.png" },
  "VF901": { "200ml": "/etichette/200ml_img/VF901.png" },
  "VF904": { "200ml": "/etichette/200ml_img/VF904.png" },
  "VF907": { "30ml": "/etichette/30ml_img/VF907.png" },
  "VF908": { "30ml": "/etichette/30ml_img/VF908.png" },
  "VF911": { "200ml": "/etichette/200ml_img/VF911.png" },
};

// Funzione per ottenere il path dell'etichetta - prima Google Drive, poi database locale
const getLabelPath = (code, format) => {
  if (!code) return null;
  
  const cleanCode = code.toString().trim().toUpperCase();
  const cleanFormat = format?.toLowerCase() || '';
  
  // 1. Prima cerca in Google Drive mapping
  const gdriveFormat = cleanFormat.replace(' ', '');  // "30ml", "50ml", etc
  if (labelMapping[gdriveFormat] && labelMapping[gdriveFormat][cleanCode]) {
    const fileId = labelMapping[gdriveFormat][cleanCode];
    // URL Google Drive thumbnail (converte PDF in PNG automaticamente)
    return `https://lh3.googleusercontent.com/d/${fileId}=w800`;
  }
  
  // 2. Fallback: cerca nel database locale
  const entry = LABELS_DATABASE[cleanCode] || LABELS_DATABASE[cleanCode.toLowerCase()];
  if (entry) {
    if (entry[cleanFormat]) {
      return entry[cleanFormat];
    }
    const availableFormats = Object.keys(entry);
    if (availableFormats.length > 0) {
      return entry[availableFormats[0]];
    }
  }
  
  return null;
};

// Channel filter configuration
const CHANNEL_FILTERS = {
  all: { label: "Tutti i canali", color: "bg-gray-100" },
  napoli: { label: "Napoli", color: "bg-purple-100 text-purple-700" },
  gls: { label: "GLS", color: "bg-yellow-100 text-yellow-700" },
  tiktok: { label: "TikTok", color: "bg-black text-white" },
  website: { label: "Sito Web", color: "bg-blue-100 text-blue-700" },
  whatsapp: { label: "WhatsApp", color: "bg-green-100 text-green-700" },
};

// Profumatori Auto disponibili
const AUTO_FRAGRANCES = [
  { code: "AUTO-ZV", name: "Zucchero a Velo", format: "200ml" },
  { code: "AUTO-BL", name: "Bianco Latte", format: "200ml" },
];

// Lettere disponibili per stampa manuale
const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "Z"];

const LabelPrint = () => {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterFormat, setFilterFormat] = useState("all");
  const [filterChannel, setFilterChannel] = useState("all");
  const [hideCompleted, setHideCompleted] = useState(true);
  
  // Tastierino manuale
  const [showManualKeypad, setShowManualKeypad] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [manualFormat, setManualFormat] = useState("100ml");
  const [manualLetter, setManualLetter] = useState("");
  const [manualQuantity, setManualQuantity] = useState(1);
  const [manualItems, setManualItems] = useState([]);
  
  // Paper selection dialog
  const [showPaperDialog, setShowPaperDialog] = useState(false);
  const [pendingPrintItems, setPendingPrintItems] = useState([]);
  const [selectedPaper, setSelectedPaper] = useState(null);

  // Pre-selected orders from Sales.jsx
  const preSelectedOrderIds = useMemo(() => {
    const ordersParam = searchParams.get("orders");
    if (ordersParam) {
      return ordersParam.split(",").filter(Boolean);
    }
    return [];
  }, [searchParams]);

  useEffect(() => {
    loadOrders();
  }, []);

  // Auto-select items from pre-selected orders
  useEffect(() => {
    if (preSelectedOrderIds.length > 0 && orders.length > 0) {
      const keysToSelect = [];
      orders.forEach(order => {
        if (preSelectedOrderIds.includes(order.id)) {
          order.items?.forEach((item, idx) => {
            const qty = item.quantity || 1;
            for (let i = 0; i < qty; i++) {
              keysToSelect.push(`${order.id}-${idx}-${i}`);
            }
          });
        }
      });
      if (keysToSelect.length > 0) {
        setSelectedItems(keysToSelect);
        toast.success(`${keysToSelect.length} etichette pre-selezionate`);
      }
    }
  }, [preSelectedOrderIds, orders]);

  const loadOrders = async () => {
    try {
      const res = await ordersAPI.getAll({ page: 1, limit: 500 });
      let ordersList = [];
      if (Array.isArray(res.data)) {
        ordersList = res.data;
      } else if (res.data?.orders && Array.isArray(res.data.orders)) {
        ordersList = res.data.orders;
      }
      setOrders(ordersList);
    } catch (error) {
      toast.error("Errore nel caricamento ordini");
      console.error(error);
    }
    setLoading(false);
  };

  // Filter orders based on channel and completion status
  const filteredOrders = useMemo(() => {
    let filtered = orders;
    
    // Always exclude cancelled/annulled orders
    filtered = filtered.filter(o => 
      o.status !== 'cancelled' && 
      o.status !== 'annullato' && 
      o.status !== 'canceled' &&
      o.status !== 'refunded'
    );
    
    // Hide completed orders by default
    if (hideCompleted) {
      filtered = filtered.filter(o => o.status !== 'complete' && o.status !== 'completed');
    }
    
    // Filter by channel/courier
    if (filterChannel !== "all") {
      if (filterChannel === "napoli") {
        filtered = filtered.filter(o => o.courier === "NAPOLI");
      } else if (filterChannel === "gls") {
        filtered = filtered.filter(o => o.courier === "GLS");
      } else if (filterChannel === "tiktok") {
        filtered = filtered.filter(o => o.channel === "tiktok_shop");
      } else if (filterChannel === "website") {
        filtered = filtered.filter(o => o.channel === "website");
      } else if (filterChannel === "whatsapp") {
        filtered = filtered.filter(o => o.channel === "whatsapp");
      }
    }
    
    return filtered;
  }, [orders, filterChannel, hideCompleted]);

  // Flatten all items from filtered orders
  const allItems = useMemo(() => {
    const items = [];
    filteredOrders.forEach(order => {
      order.items?.forEach((item, idx) => {
        const qty = item.quantity || 1;
        for (let i = 0; i < qty; i++) {
          items.push({
            orderId: order.id,
            orderName: order.customer_name || 'N/A',
            orderNumber: order.woo_order_id,
            orderChannel: order.channel,
            orderCourier: order.courier,
            orderStatus: order.status,
            itemIndex: idx,
            subIndex: i,
            item: item,
            format: item.format,
            perfumeCode: item.perfume_code,
            perfumeName: item.perfume_name,
            uniqueKey: `${order.id}-${idx}-${i}`,
            isManual: false,
          });
        }
      });
    });
    return items;
  }, [filteredOrders]);

  // Filter items based on search and format
  const filteredItems = useMemo(() => {
    let filtered = allItems;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.orderName?.toLowerCase().includes(query) ||
        item.perfumeCode?.toLowerCase().includes(query) ||
        item.perfumeName?.toLowerCase().includes(query) ||
        item.orderNumber?.toString().includes(query)
      );
    }
    
    if (filterFormat !== "all") {
      filtered = filtered.filter(item => item.format === filterFormat);
    }
    
    return filtered;
  }, [allItems, searchQuery, filterFormat]);

  // Group items by paper type
  const itemsByPaper = useMemo(() => {
    const small = filteredItems.filter(item => 
      PAPER_GROUPS.small.formats.includes(item.format)
    );
    const large = filteredItems.filter(item => 
      PAPER_GROUPS.large.formats.includes(item.format)
    );
    return { small, large };
  }, [filteredItems]);

  const handleSelectItem = (uniqueKey) => {
    setSelectedItems(prev => 
      prev.includes(uniqueKey)
        ? prev.filter(k => k !== uniqueKey)
        : [...prev, uniqueKey]
    );
  };

  const handleSelectAll = (paperType) => {
    const items = paperType === 'small' ? itemsByPaper.small : itemsByPaper.large;
    const keys = items.map(i => i.uniqueKey);
    
    const allSelected = keys.every(k => selectedItems.includes(k));
    
    if (allSelected) {
      setSelectedItems(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelectedItems(prev => [...new Set([...prev, ...keys])]);
    }
  };

  // Add manual item to the list
  const handleAddManualItem = () => {
    if (!manualCode.trim()) {
      toast.error("Inserisci un codice profumo");
      return;
    }
    
    const codeWithLetter = manualLetter ? `${manualCode}${manualLetter}` : manualCode;
    
    for (let i = 0; i < manualQuantity; i++) {
      const newItem = {
        uniqueKey: `manual-${Date.now()}-${i}`,
        perfumeCode: codeWithLetter,
        perfumeName: "",
        format: manualFormat,
        orderName: "Stampa Manuale",
        isManual: true,
      };
      setManualItems(prev => [...prev, newItem]);
    }
    
    toast.success(`Aggiunto ${manualQuantity}x ${codeWithLetter} (${manualFormat})`);
    setManualCode("");
    setManualLetter("");
    setManualQuantity(1);
  };

  // Add auto fragrance
  const handleAddAutoFragrance = (auto) => {
    for (let i = 0; i < manualQuantity; i++) {
      const newItem = {
        uniqueKey: `manual-auto-${Date.now()}-${i}`,
        perfumeCode: auto.code,
        perfumeName: auto.name,
        format: auto.format,
        orderName: "Profumatore Auto",
        isManual: true,
      };
      setManualItems(prev => [...prev, newItem]);
    }
    toast.success(`Aggiunto ${manualQuantity}x ${auto.name}`);
    setManualQuantity(1);
  };

  // Remove manual item
  const handleRemoveManualItem = (uniqueKey) => {
    setManualItems(prev => prev.filter(i => i.uniqueKey !== uniqueKey));
  };

  const handlePrintSelected = () => {
    // Combine selected order items + manual items
    const selectedOrderItems = allItems.filter(i => selectedItems.includes(i.uniqueKey));
    const allToPrint = [...selectedOrderItems, ...manualItems];
    
    if (allToPrint.length === 0) {
      toast.error("Seleziona almeno un articolo da stampare");
      return;
    }
    
    // Check if mixed paper types
    const hasSmall = allToPrint.some(i => PAPER_GROUPS.small.formats.includes(i.format));
    const hasLarge = allToPrint.some(i => PAPER_GROUPS.large.formats.includes(i.format));
    
    if (hasSmall && hasLarge) {
      toast.warning("Hai selezionato formati che richiedono carte diverse. Verranno stampati separatamente.");
    }
    
    setPendingPrintItems(allToPrint);
    setShowPaperDialog(true);
  };

  const handleConfirmPrint = () => {
    if (!selectedPaper) {
      toast.error("Seleziona il tipo di carta inserita");
      return;
    }
    
    const paperGroup = PAPER_GROUPS[selectedPaper];
    const itemsForPaper = pendingPrintItems.filter(i => 
      paperGroup.formats.includes(i.format)
    );
    
    if (itemsForPaper.length === 0) {
      toast.error(`Nessun articolo da stampare per ${paperGroup.name}`);
      return;
    }
    
    generateLabelPrint(itemsForPaper, selectedPaper);
    
    // Check if there are remaining items for other paper
    const remainingItems = pendingPrintItems.filter(i => 
      !paperGroup.formats.includes(i.format)
    );
    
    if (remainingItems.length > 0) {
      setPendingPrintItems(remainingItems);
      setSelectedPaper(null);
      toast.info(`Rimangono ${remainingItems.length} etichette per l'altra carta`);
    } else {
      setShowPaperDialog(false);
      setPendingPrintItems([]);
      setSelectedPaper(null);
      setSelectedItems([]);
      setManualItems([]);
    }
  };

  // ============== ESPORTA PER NICELABEL ==============
  const exportForNiceLabel = () => {
    const selectedOrderItems = allItems.filter(i => selectedItems.includes(i.uniqueKey));
    const allToPrint = [...selectedOrderItems, ...manualItems];
    
    if (allToPrint.length === 0) {
      toast.error("Seleziona almeno un articolo da esportare");
      return;
    }
    
    // Genera CSV per NiceLabel
    // Colonne: Codice, Nome, Formato, URL_Immagine, Quantita
    const headers = ["Codice", "Nome", "Formato", "URL_Immagine"];
    
    const rows = allToPrint.map(item => {
      const code = item.perfumeCode?.trim() || '';
      const name = item.perfumeName || '';
      const format = item.format?.toLowerCase() || '';
      
      // Ottieni URL immagine da Google Drive
      const gdriveFormat = format.replace(' ', '');
      let imageUrl = '';
      if (labelMapping[gdriveFormat] && labelMapping[gdriveFormat][code.toUpperCase()]) {
        const fileId = labelMapping[gdriveFormat][code.toUpperCase()];
        imageUrl = `https://lh3.googleusercontent.com/d/${fileId}=w800`;
      }
      
      return [code, name, format, imageUrl].map(val => `"${val}"`).join(',');
    });
    
    const csvContent = [headers.join(','), ...rows].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `etichette_nicelabel_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`Esportate ${allToPrint.length} etichette per NiceLabel`);
  };

  const generateLabelPrint = (items, paperType) => {
    const paperGroup = PAPER_GROUPS[paperType];
    
    // Genera HTML con immagini PNG delle etichette usando configurazione per formato
    const labelsHtml = items.map((item, index) => {
      const code = item.perfumeCode?.trim() || '';
      const format = item.format?.toLowerCase() || '';
      
      // Ottieni configurazione specifica per questo formato
      const labelConfig = LABEL_CONFIG[format] || LABEL_CONFIG["100ml"]; // fallback
      const { widthMM, heightMM, orientation, autoCut } = labelConfig;
      
      // Per landscape, scambia width e height nella visualizzazione
      const displayWidth = orientation === 'landscape' ? heightMM : widthMM;
      const displayHeight = orientation === 'landscape' ? widthMM : heightMM;
      
      // Determina se mostrare il numero ordine - DISABILITATO per evitare riquadro nero
      const showOrderNumber = false; // Rimosso per stampa pulita
      const orderNumberHtml = '';
      
      // Usa il database per trovare il path corretto
      const imgPath = getLabelPath(code, format);
      
      // Page break solo se autoCut è abilitato, altrimenti continuous
      const pageBreakStyle = autoCut ? 'page-break-after: always;' : '';
      
      // Rotazione per landscape
      const rotateStyle = orientation === 'landscape' ? 'transform: rotate(0deg);' : '';
      
      if (imgPath) {
        // Etichetta trovata nel database
        return `
          <div class="label-page format-${format.replace('ml', '')}" style="
            width: ${displayWidth}mm;
            height: ${displayHeight}mm;
            ${pageBreakStyle}
            page-break-inside: avoid;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            background: white;
            position: relative;
            ${rotateStyle}
          ">
            ${orderNumberHtml}
            <img 
              src="${imgPath}" 
              alt="Etichetta ${code}"
              style="
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
              "
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            />
            <div class="fallback-label" style="
              display: none;
              width: 100%;
              height: 100%;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              border: 2px dashed #C9A227;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
              background: #fffef5;
            ">
              <div style="font-size: ${paperType === 'large' ? '42px' : '28px'}; font-weight: bold; color: #C9A227;">
                ${code}
              </div>
              <div style="font-size: ${paperType === 'large' ? '22px' : '16px'}; color: #333; margin-top: 8px;">
                ${item.format}
              </div>
              <div style="font-size: ${paperType === 'large' ? '12px' : '10px'}; color: #c00; margin-top: 5px;">
                Immagine non caricata
              </div>
            </div>
          </div>
        `;
      } else {
        // Etichetta NON trovata nel database - mostra fallback con codice grande
        return `
          <div class="label-page format-${format.replace('ml', '')}" style="
            width: ${displayWidth}mm;
            height: ${displayHeight}mm;
            ${pageBreakStyle}
            page-break-inside: avoid;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            background: white;
            position: relative;
            ${rotateStyle}
          ">
            ${orderNumberHtml}
            <div style="
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              border: 3px solid #C9A227;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
              background: linear-gradient(135deg, #fffef5 0%, #fff9e6 100%);
            ">
              <div style="font-size: ${paperType === 'large' ? '48px' : '32px'}; font-weight: bold; color: #C9A227; text-shadow: 1px 1px 2px rgba(0,0,0,0.1);">
                ${code}
              </div>
              <div style="font-size: ${paperType === 'large' ? '24px' : '18px'}; color: #333; margin-top: 10px; font-weight: 500;">
                ${item.format}
              </div>
              <div style="font-size: ${paperType === 'large' ? '14px' : '10px'}; color: #999; margin-top: 8px;">
                EQUIVALENCIA
              </div>
            </div>
          </div>
        `;
      }
    }).join('');
    
    // Determina le dimensioni della pagina in base al primo formato nel gruppo
    const firstFormat = items[0]?.format?.toLowerCase() || '100ml';
    const firstConfig = LABEL_CONFIG[firstFormat] || LABEL_CONFIG["100ml"];
    const pageWidth = firstConfig.orientation === 'landscape' ? firstConfig.heightMM : firstConfig.widthMM;
    const pageHeight = firstConfig.orientation === 'landscape' ? firstConfig.widthMM : firstConfig.heightMM;
    
    const html = `<!DOCTYPE html>
    <html>
    <head>
      <title>Etichette - ${paperGroup.name}</title>
      <style>
        /* Configurazione per Epson ColorWorks C4000e */
        @page {
          size: ${pageWidth}mm ${pageHeight}mm;
          margin: 0;
        }
        
        /* Configurazioni specifiche per formato */
        @page format-30 {
          size: 40mm 60mm;
          margin: 0;
        }
        @page format-50 {
          size: 60mm 40mm;
          margin: 0;
        }
        @page format-100, @page format-200, @page format-500 {
          size: 80mm 101mm;
          margin: 0;
        }
        
        @media print {
          body { 
            margin: 0; 
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color-adjust: exact;
          }
          .no-print { display: none !important; }
          
          /* Formato 30ml - Verticale, NO taglio */
          .format-30 {
            page: format-30;
            width: 40mm !important;
            height: 60mm !important;
          }
          
          /* Formato 50ml - Orizzontale, NO taglio */
          .format-50 {
            page: format-50;
            width: 60mm !important;
            height: 40mm !important;
          }
          
          /* Formato 100ml/200ml/500ml - Verticale, CON taglio */
          .format-100, .format-200, .format-500 {
            page: format-100;
            width: 80mm !important;
            height: 101mm !important;
            page-break-after: always;
          }
          
          .label-page {
            page-break-inside: avoid;
          }
          .label-page:last-child {
            page-break-after: auto;
          }
        }
        
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }
        .print-header {
          padding: 20px;
          text-align: center;
          border-bottom: 2px solid #C9A227;
          background: #f9f9f9;
        }
        .print-header h2 {
          margin: 0 0 10px 0;
          color: #333;
        }
        .print-info {
          display: flex;
          justify-content: center;
          gap: 20px;
          font-size: 14px;
          color: #666;
          flex-wrap: wrap;
        }
        .print-info span {
          background: #e5e5e5;
          padding: 5px 15px;
          border-radius: 20px;
          margin: 5px;
        }
        .labels-container {
          padding: 10px;
        }
        .print-btn {
          background: #C9A227;
          color: white;
          border: none;
          padding: 12px 30px;
          font-size: 16px;
          border-radius: 8px;
          cursor: pointer;
          margin-top: 15px;
        }
        .print-btn:hover {
          background: #b08d1f;
        }
        .config-info {
          margin-top: 15px;
          padding: 10px;
          background: #fff3cd;
          border-radius: 8px;
          font-size: 12px;
          color: #856404;
        }
      </style>
    </head>
    <body>
      <div class="print-header no-print">
        <h2>🏷️ Etichette Prodotto - Equivalencia</h2>
        <div class="print-info">
          <span>📦 ${paperGroup.name}</span>
          <span>🏷️ ${items.length} etichette</span>
          <span>🖨️ Epson C4000e</span>
        </div>
        <div class="config-info no-print">
          <strong>⚙️ Configurazione Stampa:</strong><br>
          ${paperType === 'small' 
            ? '30ml: 40×60mm Verticale | 50ml: 60×40mm Orizzontale | Uscita continua (no taglio)' 
            : '100ml/200ml: 80×101mm Verticale | Taglio automatico dopo ogni etichetta'}
        </div>
        <button class="print-btn" onclick="window.print()">🖨️ Stampa Etichette</button>
      </div>
      <div class="labels-container">
        ${labelsHtml}
      </div>
    </body>
    </html>`;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      toast.success(`Pronte ${items.length} etichette (${paperGroup.name}) per Epson C4000e`);
    } else {
      toast.error("Popup bloccato. Abilita i popup per stampare.");
    }
  };

  // Count selected by paper type
  const selectedCounts = useMemo(() => {
    const selectedDetails = allItems.filter(i => selectedItems.includes(i.uniqueKey));
    const manualSmall = manualItems.filter(i => PAPER_GROUPS.small.formats.includes(i.format)).length;
    const manualLarge = manualItems.filter(i => PAPER_GROUPS.large.formats.includes(i.format)).length;
    
    return {
      small: selectedDetails.filter(i => PAPER_GROUPS.small.formats.includes(i.format)).length + manualSmall,
      large: selectedDetails.filter(i => PAPER_GROUPS.large.formats.includes(i.format)).length + manualLarge,
      total: selectedDetails.length + manualItems.length,
    };
  }, [selectedItems, allItems, manualItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#C9A227] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="label-print-page" className="animate-fadeIn">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Stampa Etichette Prodotto</h1>
            <p className="text-gray-500 text-sm mt-1">Seleziona gli articoli e stampa le etichette per formato</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowManualKeypad(!showManualKeypad)}
              className={showManualKeypad ? "bg-purple-100 border-purple-300" : ""}
              data-testid="btn-toggle-keypad"
            >
              <Keyboard className="w-4 h-4 mr-2" />
              Tastierino
              {showManualKeypad ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
            
            {selectedCounts.total > 0 && (
              <>
                <Button 
                  onClick={handlePrintSelected}
                  className="bg-[#C9A227] hover:bg-[#b08d1f] text-white"
                  data-testid="btn-print-labels"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Stampa {selectedCounts.total} Etichette
                </Button>
                
                <Button 
                  onClick={exportForNiceLabel}
                  variant="outline"
                  className="border-purple-500 text-purple-600 hover:bg-purple-50"
                  data-testid="btn-export-nicelabel"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Esporta NiceLabel
                </Button>
              </>
            )}
          </div>
        </div>
        
        {/* Manual Keypad - Collapsible */}
        {showManualKeypad && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4" data-testid="manual-keypad">
            <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <Keyboard className="w-5 h-5" />
              Stampa Manuale
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left: Code input */}
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                    placeholder="Codice profumo (es: 123)"
                    className="flex-1"
                    data-testid="manual-code-input"
                  />
                  <Select value={manualFormat} onValueChange={setManualFormat}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30ml">30ml</SelectItem>
                      <SelectItem value="50ml">50ml</SelectItem>
                      <SelectItem value="100ml">100ml</SelectItem>
                      <SelectItem value="200ml">200ml</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Letters */}
                <div>
                  <p className="text-xs text-purple-600 mb-1">Lettera (opzionale)</p>
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => setManualLetter("")}
                      className={`px-2 py-1 text-xs rounded ${!manualLetter ? 'bg-purple-600 text-white' : 'bg-white border border-purple-200'}`}
                    >
                      Nessuna
                    </button>
                    {LETTERS.map(letter => (
                      <button
                        key={letter}
                        onClick={() => setManualLetter(letter)}
                        className={`w-7 h-7 text-xs rounded ${manualLetter === letter ? 'bg-purple-600 text-white' : 'bg-white border border-purple-200 hover:bg-purple-100'}`}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Quantity + Add button */}
                <div className="flex gap-2 items-center">
                  <span className="text-sm text-purple-700">Quantità:</span>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={manualQuantity}
                    onChange={(e) => setManualQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20"
                    data-testid="manual-quantity-input"
                  />
                  <Button 
                    onClick={handleAddManualItem}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="btn-add-manual"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi
                  </Button>
                </div>
              </div>
              
              {/* Right: Auto fragrances */}
              <div>
                <p className="text-xs text-purple-600 mb-2 flex items-center gap-1">
                  <Car className="w-4 h-4" /> Profumatori Auto
                </p>
                <div className="space-y-2">
                  {AUTO_FRAGRANCES.map(auto => (
                    <button
                      key={auto.code}
                      onClick={() => handleAddAutoFragrance(auto)}
                      className="w-full p-2 bg-white border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center justify-between"
                      data-testid={`btn-auto-${auto.code}`}
                    >
                      <span className="font-medium text-[#C9A227]">{auto.code}</span>
                      <span className="text-gray-600 text-sm">{auto.name}</span>
                      <span className="text-xs text-gray-400">{auto.format}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Manual items list */}
            {manualItems.length > 0 && (
              <div className="mt-4 pt-3 border-t border-purple-200">
                <p className="text-sm font-medium text-purple-700 mb-2">
                  Etichette manuali ({manualItems.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {manualItems.map(item => (
                    <div
                      key={item.uniqueKey}
                      className="flex items-center gap-2 bg-white px-3 py-1 rounded-lg border border-purple-200"
                    >
                      <span className="font-bold text-[#C9A227]">{item.perfumeCode}</span>
                      <span className="text-sm text-gray-500">{item.format}</span>
                      <button
                        onClick={() => handleRemoveManualItem(item.uniqueKey)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Search and filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Cerca per nome, codice profumo, n° ordine..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="label-search"
            />
          </div>
          
          <Select value={filterChannel} onValueChange={setFilterChannel}>
            <SelectTrigger className="w-[150px]" data-testid="filter-channel">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Canale" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CHANNEL_FILTERS).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterFormat} onValueChange={setFilterFormat}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Formato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="30ml">30ml</SelectItem>
              <SelectItem value="50ml">50ml</SelectItem>
              <SelectItem value="100ml">100ml</SelectItem>
              <SelectItem value="200ml">200ml</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant={hideCompleted ? "default" : "outline"}
            size="sm"
            onClick={() => setHideCompleted(!hideCompleted)}
            className={hideCompleted ? "bg-green-600 hover:bg-green-700" : ""}
            data-testid="btn-hide-completed"
          >
            {hideCompleted ? "✓ Nascondi completati" : "Mostra completati"}
          </Button>
        </div>
        
        {/* Selection summary */}
        {selectedCounts.total > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-[#C9A227]/10 rounded-lg">
            <span className="text-sm font-medium text-[#C9A227]">Selezionati:</span>
            {selectedCounts.small > 0 && (
              <Badge className={PAPER_GROUPS.small.color}>
                {selectedCounts.small} × Carta A (30/50ml)
              </Badge>
            )}
            {selectedCounts.large > 0 && (
              <Badge className={PAPER_GROUPS.large.color}>
                {selectedCounts.large} × Carta B (100/200ml)
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Items grouped by paper type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Small formats (30ml, 50ml) */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className={`p-4 ${PAPER_GROUPS.small.color} flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              <span className="font-semibold">{PAPER_GROUPS.small.name}</span>
              <Badge variant="secondary">{itemsByPaper.small.length}</Badge>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => handleSelectAll('small')}
              className="text-blue-700 hover:bg-blue-200"
            >
              {itemsByPaper.small.every(i => selectedItems.includes(i.uniqueKey)) && itemsByPaper.small.length > 0
                ? 'Deseleziona tutti'
                : 'Seleziona tutti'}
            </Button>
          </div>
          
          <div className="p-4 max-h-[500px] overflow-y-auto">
            {itemsByPaper.small.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Nessun articolo 30ml o 50ml</p>
            ) : (
              <div className="space-y-2">
                {itemsByPaper.small.map(item => (
                  <div
                    key={item.uniqueKey}
                    onClick={() => handleSelectItem(item.uniqueKey)}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedItems.includes(item.uniqueKey)
                        ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                        : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      {selectedItems.includes(item.uniqueKey) 
                        ? <CheckSquare className="w-5 h-5 text-blue-600" />
                        : <Square className="w-5 h-5 text-gray-400" />
                      }
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#C9A227]">{item.perfumeCode}</span>
                          <Badge variant="outline" className="text-xs">{item.format}</Badge>
                          {item.orderCourier && (
                            <Badge className={item.orderCourier === 'GLS' ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'}>
                              {item.orderCourier}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{item.orderName}</p>
                      </div>
                      {item.orderNumber && (
                        <span className="text-xs text-gray-400">#{item.orderNumber}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Large formats (100ml, 200ml) */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className={`p-4 ${PAPER_GROUPS.large.color} flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              <span className="font-semibold">{PAPER_GROUPS.large.name}</span>
              <Badge variant="secondary">{itemsByPaper.large.length}</Badge>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => handleSelectAll('large')}
              className="text-green-700 hover:bg-green-200"
            >
              {itemsByPaper.large.every(i => selectedItems.includes(i.uniqueKey)) && itemsByPaper.large.length > 0
                ? 'Deseleziona tutti'
                : 'Seleziona tutti'}
            </Button>
          </div>
          
          <div className="p-4 max-h-[500px] overflow-y-auto">
            {itemsByPaper.large.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Nessun articolo 100ml o 200ml</p>
            ) : (
              <div className="space-y-2">
                {itemsByPaper.large.map(item => (
                  <div
                    key={item.uniqueKey}
                    onClick={() => handleSelectItem(item.uniqueKey)}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedItems.includes(item.uniqueKey)
                        ? 'bg-green-50 border-green-300 ring-2 ring-green-200'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      {selectedItems.includes(item.uniqueKey) 
                        ? <CheckSquare className="w-5 h-5 text-green-600" />
                        : <Square className="w-5 h-5 text-gray-400" />
                      }
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#C9A227]">{item.perfumeCode}</span>
                          <Badge variant="outline" className="text-xs">{item.format}</Badge>
                          {item.orderCourier && (
                            <Badge className={item.orderCourier === 'GLS' ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'}>
                              {item.orderCourier}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{item.orderName}</p>
                      </div>
                      {item.orderNumber && (
                        <span className="text-xs text-gray-400">#{item.orderNumber}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Paper Selection Dialog */}
      <Dialog open={showPaperDialog} onOpenChange={setShowPaperDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C9A227]" />
              Seleziona Carta Inserita
            </DialogTitle>
            <DialogDescription>
              Quale carta hai inserito nella stampante?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {Object.entries(PAPER_GROUPS).map(([key, group]) => {
              const count = pendingPrintItems.filter(i => group.formats.includes(i.format)).length;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedPaper(key)}
                  disabled={count === 0}
                  className={`
                    w-full p-4 rounded-lg border-2 text-left transition-all
                    ${selectedPaper === key
                      ? 'border-[#C9A227] bg-[#C9A227]/10'
                      : count === 0
                        ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-[#C9A227]/50'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{group.name}</p>
                      <p className="text-sm text-gray-500">Formati: {group.formats.join(', ')}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Dimensione: {group.widthMM}mm × {group.heightMM}mm
                      </p>
                    </div>
                    <Badge className={group.color}>{count} etichette</Badge>
                  </div>
                </button>
              );
            })}
          </div>
          
          {pendingPrintItems.some(i => PAPER_GROUPS.small.formats.includes(i.format)) &&
           pendingPrintItems.some(i => PAPER_GROUPS.large.formats.includes(i.format)) && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Hai selezionato formati misti. Dovrai stampare due volte con carte diverse.</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaperDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleConfirmPrint}
              disabled={!selectedPaper}
              className="bg-[#C9A227] hover:bg-[#b08d1f]"
            >
              <Printer className="w-4 h-4 mr-2" />
              Stampa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabelPrint;
